import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { MultiFactorError } from "firebase/auth";
import type { Status } from "types/Status";
import type OutputError from "config/OutputError";
import { useUserContext } from "hooks";
import UserService from "services/UserService";
import type { Unsubscribe } from "firebase/app-check";
import type { User } from "firebase/auth";

export type AuthError = null | OutputError | MultiFactorError;

export interface AuthVerifierContextType {
  status: Status;
  error: AuthError;
  data: boolean;
  onSetError: (error: AuthError) => void;
}

export const AuthVerifierContext = createContext<AuthVerifierContextType | undefined>(undefined);
AuthVerifierContext.displayName = "AuthVerifierContext";

interface AuthVerifierProviderProps {
  children?: React.ReactNode;
}

/**
 * AuthVerifierProvider owns the single Firebase auth listener for the app and shares
 * its result with every route guard and the login screen.
 */
function AuthVerifierProvider(props: AuthVerifierProviderProps) {
  const [{ user }, { onLoginSuccess, onSignOut }] = useUserContext();

  const [status, setStatus] = useState<Status>("fetching");
  const [error, setError] = useState<AuthError>(null);

  useEffect(() => {
    let authUnsubscribe: Unsubscribe;

    (async () => {
      const params = new URLSearchParams(window.location.search);

      const loginToken = params.get("loginToken");

      if (loginToken) {
        try {
          await UserService.signOut();
          await UserService.signInWithCustomToken(loginToken);
        } catch (error: unknown) {
          setStatus("error");
          setError(error as AuthError);
        }

        // Clearing the query params on token login
        // outside of the react-router lifecycle
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("loginToken"); // Delete loginToken only
        window.history.replaceState({}, "", newUrl.href); // Replace the current URL without a page reload
      }

      authUnsubscribe = UserService.onAuthStateChanged(async (user: User) => {
        setStatus("fetching");

        if (user) {
          try {
            await UserService.validateSigninClaims();
            await onLoginSuccess(user);
          } catch (loginError: unknown) {
            setError(loginError as AuthError);
            setStatus("error");
            await onSignOut();
          }
        }
        setStatus("success");
      });
    })();

    return () => {
      authUnsubscribe && authUnsubscribe();
    };
  }, [onLoginSuccess, onSignOut]);

  const onSetError = useCallback((error: AuthError) => {
    setError(error);
    setStatus("error");
  }, []);

  const value = useMemo(() => ({ status, error, data: Boolean(user), onSetError }), [status, error, user, onSetError]);

  return <AuthVerifierContext.Provider value={value}>{props.children}</AuthVerifierContext.Provider>;
}

export default AuthVerifierProvider;
